import { useEffect } from 'react';
import KpiAxisConfigurationBinCountField from 'components/molecules/PackArchitecture/KpiAxisConfigurationBinCountField';
import KpiAxisConfigurationBreakpointsField from 'components/molecules/PackArchitecture/KpiAxisConfigurationBreakpointsField';
import KpiAxisConfigurationBinsField from 'components/molecules/PackArchitecture/KpiAxisConfigurationBinsField';
import KpiRangeSlider from 'components/molecules/PackArchitecture/KpiRangeSlider';
import type {
    KpiAxisConfiguration,
    KpiBinLabelAffixes,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import {
    buildKpiPreview,
    hasKpiBinSizeError,
    sanitizeKpiBreakpoints,
    type KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import type { KpiAxisInteractionSource } from 'components/molecules/PackArchitecture/kpiAxisConfigurationTracking.helpers';
import { useKpiRangeInput } from 'components/molecules/PackArchitecture/useKpiRangeInput';

const SectionDivider = () => <div className="border-t border-backgroundLightGrey" />;

type KpiAxisConfigurationModalConfigurationControlsProps = {
    config: KpiAxisConfiguration;
    controlsMeta: KpiAxisControlsMeta;
    dataRange: KpiDataRange;
    defaultBinCount: number;
    binLabelAffixes?: KpiBinLabelAffixes;
    onChange: (patch: Partial<KpiAxisConfiguration>) => void;
    onRangeInteraction?: (source: KpiAxisInteractionSource, min: number, max: number) => void;
    onBreakpointInteraction?: (source: KpiAxisInteractionSource, breakpoints: number[]) => void;
    onKpiPreviewPauseChange?: (paused: boolean) => void;
};

const KpiAxisConfigurationModalConfigurationControls = ({
    config,
    controlsMeta,
    dataRange,
    defaultBinCount,
    binLabelAffixes,
    onChange,
    onRangeInteraction,
    onBreakpointInteraction,
    onKpiPreviewPauseChange,
}: KpiAxisConfigurationModalConfigurationControlsProps) => {
    const preview = buildKpiPreview(config, binLabelAffixes);
    const isCustomBinning = config.binningMethod === 'custom';
    const sliderBreakpoints = isCustomBinning ? config.breakpoints : preview.breakpoints;
    const { minInputError, maxInputError, applyMinValue, applyMaxValue, updateRangeFromSlider } = useKpiRangeInput({
        dataRange,
        config,
        onChange,
        onRangeInteraction,
    });
    const hasBinSizeError = !isCustomBinning && hasKpiBinSizeError(config.min, config.max, config.binCount);

    useEffect(() => {
        onKpiPreviewPauseChange?.(Boolean(minInputError || maxInputError || hasBinSizeError));
    }, [hasBinSizeError, maxInputError, minInputError, onKpiPreviewPauseChange]);

    const handleBinningMethodChange = (binningMethod: KpiAxisConfiguration['binningMethod']) => {
        if (binningMethod === 'uniform') {
            onChange({
                binningMethod: 'uniform',
                binCount: config.binCount || defaultBinCount,
            });
            return;
        }

        onChange({ binningMethod: 'custom' });
    };

    const handleBreakpointsChange = (breakpoints: number[], source: KpiAxisInteractionSource) => {
        const nextBreakpoints = sanitizeKpiBreakpoints(breakpoints, config.min, config.max, dataRange);
        onBreakpointInteraction?.(source, nextBreakpoints);
        onChange({ breakpoints: nextBreakpoints });
    };

    const handleRevertToUniform = () => {
        onChange({
            binningMethod: 'uniform',
            binCount: defaultBinCount,
            breakpoints: [],
        });
    };

    return (
        <div className="flex h-full w-[476px] shrink-0 flex-col gap-6 border-r border-backgroundLightGrey bg-white p-6">
            <div className="flex flex-col gap-4">
                <p className="m-0 text-base font-medium leading-6 text-blackTitle">{controlsMeta.range.label}</p>
                <KpiRangeSlider
                    dataRange={dataRange}
                    min={config.min}
                    max={config.max}
                    binningMethod={config.binningMethod}
                    breakpoints={sliderBreakpoints}
                    minInputError={minInputError}
                    maxInputError={maxInputError}
                    minInputLabel={controlsMeta.range.minInput.label}
                    maxInputLabel={controlsMeta.range.maxInput.label}
                    onRangeChange={updateRangeFromSlider}
                    onMinInput={applyMinValue}
                    onMaxInput={applyMaxValue}
                    onBreakpointsChange={
                        isCustomBinning
                            ? (breakpoints) => handleBreakpointsChange(breakpoints, 'slider')
                            : undefined
                    }
                />
            </div>

            <SectionDivider />

            <div className="flex flex-col gap-6">
                <KpiAxisConfigurationBinsField
                    controlsMeta={controlsMeta.bins}
                    value={config.binningMethod}
                    onChange={handleBinningMethodChange}
                />
                {isCustomBinning ? (
                    <KpiAxisConfigurationBreakpointsField
                        min={config.min}
                        max={config.max}
                        breakpoints={config.breakpoints}
                        dataRange={dataRange}
                        breakPointLabel={controlsMeta.bins.custom.breakPoint.label}
                        onBreakpointsChange={(breakpoints) => handleBreakpointsChange(breakpoints, 'input')}
                        onRevertToUniform={handleRevertToUniform}
                    />
                ) : (
                    <KpiAxisConfigurationBinCountField
                        min={config.min}
                        max={config.max}
                        binCount={config.binCount}
                        binSize={preview.binSize}
                        countLabel={controlsMeta.bins.uniform.count.label}
                        sizeLabel={controlsMeta.bins.uniform.size.label}
                        onBinCountChange={(binCount) => onChange({ binCount })}
                    />
                )}
            </div>
        </div>
    );
};

export default KpiAxisConfigurationModalConfigurationControls;
